// Winback SİTESİ veri katmanı (sahip 2026-10-07) — yalnız sitenin kendi uçları: oturum, kendi
// şifresi, kullanıcı yönetimi (src/winback-site/routes.js). Analiz çağrısı panelle ortak
// (../api getWinback — aynı yol, bu sitenin origin'inde). Oturum httpOnly `wb_sid` çerezi;
// 401 → `caa:unauthorized` olayı (../api ile aynı olay, SiteApp ikisini de dinler).

import { ApiError } from '../api';

export type SiteRole = 'admin' | 'user';

/** /api/auth/me — oturum açmış site kullanıcısı (şifre asla taşınmaz). */
export interface SiteUser {
  username: string;
  displayName: string;
  role: SiteRole;
  roleLabel: string;
  /** BackOffice'i yapılandırılmış markalar (sunucu configuredBrands). */
  brands: string[];
  active: boolean;
}

/** /api/users satırı. */
export interface SiteUserRow {
  username: string;
  displayName: string;
  role: SiteRole;
  roleLabel: string;
  active: boolean;
  createdAt: string;
}

export interface SiteResult {
  ok: boolean;
  error?: string;
  user?: SiteUserRow;
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { credentials: 'include', ...init });
  } catch {
    throw new ApiError(0, 'network_error');
  }
  if (res.status === 401 && !path.startsWith('/api/auth/login')) {
    window.dispatchEvent(new Event('caa:unauthorized'));
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) {
    let msg = 'request_failed';
    try {
      const body = (await res.json()) as { error?: string };
      if (body && body.error) msg = body.error;
    } catch {
      /* gövde JSON değilse genel mesaj */
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

/** Sayfa açılışında oturumu doğrular. 401 → null. */
export async function siteMe(): Promise<SiteUser | null> {
  try {
    return await http<SiteUser>('/api/auth/me');
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export async function siteLogout(): Promise<void> {
  try {
    await http<{ ok: boolean }>('/api/auth/logout', { method: 'POST' });
  } catch {
    /* oturum zaten düşmüş olabilir */
  }
}

export function changeOwnPassword(currentPassword: string, newPassword: string): Promise<SiteResult> {
  return http<SiteResult>('/api/auth/password', jsonInit('POST', { currentPassword, newPassword }));
}

export function listUsers(): Promise<SiteUserRow[]> {
  return http<SiteUserRow[]>('/api/users');
}

export function createUser(input: { username: string; displayName: string; password: string; role: SiteRole }): Promise<SiteResult> {
  return http<SiteResult>('/api/users', jsonInit('POST', input));
}

export function updateUser(username: string, patch: { displayName?: string; role?: SiteRole; active?: boolean; password?: string }): Promise<SiteResult> {
  return http<SiteResult>('/api/users/' + encodeURIComponent(username), jsonInit('PATCH', patch));
}

export function deleteUser(username: string): Promise<SiteResult> {
  return http<SiteResult>('/api/users/' + encodeURIComponent(username), { method: 'DELETE' });
}
